import { Controller } from '@hotwired/stimulus';
import { patchOrder } from './tabulator_reorder/patch_order';

/**
 * Moves a whole group of a `grouped_rows:` Tabulator table up or down.
 *
 * Tabulator has no group drag — groups are drop receivers only — so group order
 * is moved with buttons in each group header. Wrap the
 * `UI::TabulatorTableComponent` render in this controller, alongside
 * `tabulator-row-reorder` if the rows are movable too.
 *
 * Nothing is applied until the server has written it: on success the declared
 * group order, the source `<tbody>` order and the position text are brought in
 * line; on failure the table is untouched and the feedback element is shown, so
 * there is nothing to revert.
 *
 * ## What the screen provides
 *
 *   value  `sort-url`  PATCH endpoint for the whole group order (required)
 *   value  `order-param`  payload key holding the order ("order")
 *   value  `announcement-template`  live-region copy with `%{position}` and
 *                     `%{total}` placeholders, substituted here
 *   target `announcement`  a `visually-hidden` `aria-live="polite"` element; a
 *                     move writes the group's new position into it
 *   target `feedback`  shown (`d-none` removed) when a save fails
 *   target `header`    a group header; its connection is what refreshes the
 *                      buttons' disabled state (headers arrive on first render
 *                      and on every group create or delete)
 *
 * ## DOM contract (all wrapper conventions, no screen vocabulary)
 *
 *   headers        `[data-group-header='<group>']`
 *   source bodies  `tbody[data-row-group='<group>']`
 *   insert anchor  `[data-row-group-anchor]` — a trailing empty `<tbody>`, since
 *                  a `<tbody>` can only be inserted next to a sibling
 *   move buttons   two `button[aria-disabled]` per header, up first then down
 *   position text  `.amba-tabulator-row-position` (optional, rewritten on save)
 */
export default class extends Controller {
  static targets = ['feedback', 'header', 'announcement'];
  static values = {
    sortUrl: String,
    orderParam: { type: String, default: 'order' },
    // Live-region copy, with `%{position}` / `%{total}` substituted here, so the
    // words stay in the locale file while the numbers come from the move.
    announcementTemplate: String,
  };

  headerTargetConnected() {
    this.refreshButtons();
  }

  moveUp(event) {
    this.move(event, -1);
  }

  moveDown(event) {
    this.move(event, 1);
  }

  move(event, delta) {
    const button = event.currentTarget;
    if (button.getAttribute('aria-disabled') === 'true') return;

    const group = button.closest('[data-group-header]')?.dataset.groupHeader;
    const order = this.currentOrder();
    const from = order.indexOf(group);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= order.length) return;

    order.splice(to, 0, ...order.splice(from, 1));
    this.persist(order, {
      announce: { position: to + 1, total: order.length },
    });
  }

  // The source `<tbody>` order is the screen's own record of the group order:
  // it is what the server rendered, and what a mutation stream swaps into.
  currentOrder() {
    return Array.from(
      this.element.querySelectorAll('tbody[data-row-group]')
    ).map((body) => body.dataset.rowGroup);
  }

  async persist(order, { announce = null } = {}) {
    const ok = await patchOrder(this.sortUrlValue, {
      sortable_settings: { [this.orderParamValue]: order },
    });

    if (ok) {
      this.hideFeedback();
      this.apply(order);
      if (announce) this.announce(announce);
    } else {
      this.showFeedback();
    }
  }

  // Clear before setting so moving a group back and forth re-announces (see
  // stimulus/accessibility.md).
  announce({ position, total }) {
    if (!this.hasAnnouncementTarget || !this.announcementTemplateValue) return;

    this.announcementTarget.textContent = '';
    this.announcementTarget.textContent = this.announcementTemplateValue
      .replace('%{position}', position)
      .replace('%{total}', total);
  }

  apply(order) {
    const anchor = this.element.querySelector('[data-row-group-anchor]');
    order.forEach((group, index) => {
      const body = this.element.querySelector(
        `tbody[data-row-group='${group}']`
      );
      // Re-inserting each `<tbody>` before the anchor in payload order rebuilds
      // the source table's group order; the anchor stays last.
      if (body && anchor) anchor.parentNode.insertBefore(body, anchor);

      this.setPosition(
        this.element.querySelector(`[data-group-header='${group}']`),
        String(index + 1)
      );
    });

    // Redeclaring the group values is what re-orders the groups on screen, and
    // it keeps the current filters and search (see the table's `setGroupOrder`).
    this.element
      .querySelector("[data-controller~='tabulator-table']")
      ?.dispatchEvent(
        new CustomEvent('tabulator-table:sync-groups', {
          detail: { groupOrder: order },
        })
      );
    this.refreshButtons();
  }

  // Only the ends of the list are barred from moving further, and which groups
  // those are changes with every reorder.
  refreshButtons() {
    const order = this.currentOrder();
    order.forEach((group, index) => {
      const header = this.element.querySelector(
        `[data-group-header='${group}']`
      );
      const buttons = header?.querySelectorAll('button[aria-disabled]');
      if (!buttons || buttons.length < 2) return;
      buttons[0].setAttribute('aria-disabled', String(index === 0));
      buttons[1].setAttribute(
        'aria-disabled',
        String(index === order.length - 1)
      );
    });
  }

  setPosition(header, position) {
    const cell = header?.querySelector('.amba-tabulator-row-position');
    if (cell) cell.textContent = position;
  }

  showFeedback() {
    if (this.hasFeedbackTarget) this.feedbackTarget.classList.remove('d-none');
  }

  hideFeedback() {
    if (this.hasFeedbackTarget) this.feedbackTarget.classList.add('d-none');
  }
}
